import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './CustomModal.scss';

export interface CustomModalProps {
  id?: string;
  isOpen: boolean;
  onRequestClose?: () => void;
  ariaHideApp?: boolean;
  children: React.ReactNode;
  style?: {
    content?: React.CSSProperties;
    overlay?: React.CSSProperties;
  };
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  className?: string;
  overlayClassName?: string;
}

const CustomModal: React.FC<CustomModalProps> = ({
  id,
  isOpen,
  onRequestClose,
  children,
  style,
  shouldCloseOnEsc = true,
  shouldCloseOnOverlayClick = true,
  className = '',
  overlayClassName = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      // Focus the modal for accessibility
      modalRef.current?.focus();
    } else {
      // Restore body scroll
      document.body.style.overflow = '';

      // Restore focus to previous element
      previousActiveElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (shouldCloseOnEsc && event.key === 'Escape' && isOpen) {
        onRequestClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, shouldCloseOnEsc, onRequestClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (shouldCloseOnOverlayClick && event.target === event.currentTarget) {
      onRequestClose?.();
    }
  };

  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent clicks inside the modal content from closing the modal
    event.stopPropagation();
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className={`custom-modal-overlay ${overlayClassName}`}
      style={style?.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={id ? `${id}-title` : undefined}
    >
      <div
        ref={modalRef}
        id={id}
        className={`custom-modal-content ${className}`}
        style={style?.content}
        onClick={handleContentClick}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );

  // Render modal in portal at document.body or #modal element
  const mountNode = document.getElementById('modal') || document.body;
  return createPortal(modalContent, mountNode);
};

export default CustomModal;
